import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, X, Search } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { BRAND } from "@/data/brand";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop JohnnyMart" },
  { to: "/gateway", label: "Gorilla Gateway" },
  { to: "/other-partners", label: "Other Partners" },
  { to: "/reviews", label: "Reviews" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/shop?search=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-cream border-b border-navy/20 shadow-sm"
    >
      <div className="jm-container flex items-center justify-between h-[72px] gap-4">
        <Link to="/" data-testid="logo-link" className="flex flex-col leading-none shrink-0">
          <span className="font-display text-2xl sm:text-3xl text-navy">JOHNNY<span className="text-brick">MART</span></span>
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-gold font-semibold">
            {BRAND.tagline}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-4 lg:gap-5 uppercase text-xs xl:text-sm font-bold tracking-wide">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `transition-colors hover:text-brick ${isActive ? "text-brick" : "text-navy"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-4">
          <form onSubmit={submitSearch} className="hidden lg:flex items-center bg-white rounded-full border border-navy/20 px-3 h-10">
            <Search className="w-4 h-4 text-navy/50" />
            <input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search treasures..."
              className="bg-transparent outline-none text-sm px-2 w-40"
            />
          </form>

          <Link
            to="/cart"
            data-testid="cart-link"
            className="relative p-2 text-navy hover:text-brick transition-colors"
          >
            <ShoppingCart className="w-6 h-6" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-1 bg-brick text-white text-[11px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
              >
                {count}
              </span>
            )}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-navy"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-cream border-t border-navy/15 px-4 pb-4" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
          <form onSubmit={submitSearch} className="flex items-center bg-white rounded-full border border-navy/20 px-3 h-11 my-3">
            <Search className="w-4 h-4 text-navy/50" />
            <input
              data-testid="mobile-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search treasures..."
              className="bg-transparent outline-none text-base px-2 flex-1"
            />
          </form>
          <nav className="flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="py-3.5 border-b border-navy/10 uppercase font-bold text-navy"
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};
